/**
 * Export System Types
 * Defines interfaces and types for the export functionality
 */

import { Model } from '../../types';

/**
 * Supported export formats
 */
export type ExportFormat = 'png' | 'jpeg' | 'svg' | 'gif' | 'json' | 'markdown';

/**
 * Export quality/resolution multiplier
 * Must be exactly 1, 2, or 4
 */
export type ExportQuality = 1 | 2 | 4;

/**
 * Type guard to check if a number is a valid ExportQuality
 */
export function isExportQuality(value: number): value is ExportQuality {
    return value === 1 || value === 2 || value === 4;
}

/**
 * Coerce a number to a valid ExportQuality (defaults to 2)
 */
export function toExportQuality(value: number): ExportQuality {
    if (value === 1) return 1;
    if (value === 4) return 4;
    return 2; // default
}

/**
 * Export configuration
 */
export interface ExportConfig {
    format: ExportFormat;
    quality?: ExportQuality;  // Changed from generic number
    transparent?: boolean;
    includeGrid?: boolean;
    backgroundColor?: string;
    filename?: string;
    exportFullDiagram?: boolean; // true = entire diagram, false = current view
    gifDurationSeconds?: number;
    gifFps?: number;
    gifLoop?: boolean;
    gifMaxColors?: number;
    abortSignal?: AbortSignal;
}

/**
 * Export result
 */
export interface ExportResult {
    success: boolean;
    data?: Blob | string;
    filename: string;
    format?: ExportFormat;
    error?: Error;
    metadata?: ExportMetadata;
}

/**
 * Export metadata
 */
export interface ExportMetadata {
    width: number;
    height: number;
    format: ExportFormat;
    quality?: ExportQuality;
    timestamp: number;
    diagramName?: string;
    version?: string;
}

/**
 * Export options for individual formats
 */
export interface PNGExportOptions {
    quality: ExportQuality;
    transparent: boolean;
    backgroundColor?: string;
    includeGrid?: boolean;
}

export interface JPEGExportOptions {
    quality: ExportQuality;
    backgroundColor: string;
    includeGrid?: boolean;
}

export interface SVGExportOptions {
    transparent?: boolean;
    includeGrid?: boolean;
    backgroundColor?: string;
}

export interface JSONExportOptions {
    pretty?: boolean;
    includeMetadata?: boolean;
}

/**
 * Export progress callback
 */
export type ExportProgressCallback = (progress: number, message: string) => void;

/**
 * Base interface for export handlers
 */
export interface ExportHandler {
    format: ExportFormat;
    export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult>;
}

export interface GIFExportOptions {
    quality: ExportQuality;
    durationSeconds: number;
    fps: number;
    loop: boolean;
    maxColors: number;
    backgroundColor?: string;
    includeGrid?: boolean;
}
